import { Button, type ButtonProps, type SxProps, type Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { forwardRef, type ComponentType } from 'react';
import { Tooltip } from '~/components/Tooltip';

export interface GridActionButtonProps extends ButtonProps {
  /** The button to render, for one with its own styling such as `DeleteButton`. */
  Button?: ComponentType<ButtonProps>;
  /** Describes the action, or why it is disabled. The button's text stays its name. */
  tooltip: string;
}

// MUI's disabled styling comes with `pointer-events: none`, which would keep the tooltip from
// opening, so the aria-disabled state is styled here instead.
const disabledSx = (theme: Theme) => ({
  '&[aria-disabled="true"]': {
    backgroundColor: theme.palette.action.disabledBackground,
    borderColor: theme.palette.action.disabledBackground,
    boxShadow: 'none',
    color: theme.palette.action.disabled,
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none',
    },
    '& .MuiButton-startIcon': {
      color: 'inherit',
    },
  },
});

type SxEntry = boolean | SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>);

// The disabled styles ahead of the caller's own, which may be one entry or a list of them.
const withDisabledSx = (sx: SxProps<Theme> | undefined): SxProps<Theme> => [
  disabledSx,
  ...(Array.isArray(sx) ? (sx as readonly SxEntry[]) : sx ? [sx as SxEntry] : []),
];

/**
 * A text button in a data grid actions cell. The cell moves focus between its direct DOM
 * children and reads `disabled` off the elements it clones, which are the row's own
 * components rather than the buttons inside them, so a disabled action here is aria-disabled
 * instead: still a bare, focusable element that holds its own tooltip and the cell's roving
 * tab stop, but one that drops its clicks. Icon actions get the same from `IconButton`.
 */
export const GridActionButton = forwardRef<HTMLButtonElement, GridActionButtonProps>(
  function GridActionButton(
    { Button: ButtonComponent = Button, disabled, disableRipple, onClick, sx, tooltip, ...props },
    ref,
  ) {
    return (
      <Tooltip title={tooltip}>
        <ButtonComponent
          {...props}
          aria-disabled={disabled || undefined}
          disableRipple={disabled || disableRipple}
          onClick={disabled ? undefined : onClick}
          ref={ref}
          sx={withDisabledSx(sx)}
        />
      </Tooltip>
    );
  },
);
